import { apiRequest } from './http';
import type {
  ApiItemResponse,
  ApiListResponse,
  GovernanceLifecycleState,
  MappingRecord,
  MappingVersionRecord,
} from '../types/api';

type Query = Record<string, string | number | boolean | undefined>;
export type LifecycleAction = 'validate' | 'approve' | 'activate' | 'reject';

const actionState: Record<LifecycleAction, GovernanceLifecycleState> = {
  validate: 'VALIDATED',
  approve: 'APPROVED',
  activate: 'ACTIVE',
  reject: 'DRAFT',
};

function queryString(query: Query = {}): string {
  const search = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== '') search.set(key, String(value));
  });
  const serialized = search.toString();
  return serialized ? `?${serialized}` : '';
}

export const mappingsApi = {
  list: (query: Query = {}) => apiRequest<ApiListResponse<MappingRecord>>(`/mappings${queryString(query)}`),
  getMapping: async (id: string) => (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}`)).data,
  createMapping: async (payload: Omit<MappingRecord, 'id' | 'createdAt' | 'updatedAt'>) =>
    (await apiRequest<ApiItemResponse<MappingRecord>>('/mappings', { method: 'POST', body: JSON.stringify(payload) })).data,
  deleteMapping: async (id: string) => apiRequest<void>(`/mappings/${id}`, { method: 'DELETE' }),
  listMappingVersions: async (id: string) =>
    (await apiRequest<ApiItemResponse<MappingVersionRecord[]>>(`/mappings/${id}/versions`)).data,
  createMappingVersion: async (
    id: string,
    payload: { ruleDefinition: Record<string, unknown>; changeLog?: string; versionLabel?: string; sourceSchemaRef?: string },
  ) =>
    (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}/versions`, {
      method: 'POST',
      body: JSON.stringify({
        versionLabel: payload.versionLabel,
        sourceSchemaRef: payload.sourceSchemaRef,
        ruleDefinition: payload.ruleDefinition,
        lifecycleState: 'DRAFT',
      }),
    })).data,
  updateMappingVersionLifecycle: async (id: string, versionId: string, action: LifecycleAction) =>
    (await apiRequest<ApiItemResponse<MappingRecord>>(`/mappings/${id}/versions/${versionId}/lifecycle`, {
      method: 'PATCH',
      body: JSON.stringify({ lifecycleState: actionState[action] }),
    })).data,
};
